import { V3SynthMarketKey, V3_SYNTH_MARKET_KEYS, v3SynthKeyToV2Asset } from '@kwenta/sdk/constants'
import { formatNumber, stripZeros } from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ValueType } from 'react-select'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import NumericInput from 'components/Input/NumericInput'
import { FlexDivRowCentered } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { V3_DEPOSITABLE_ASSETS, V3_DEPOSIT_OPTIONS } from 'constants/perpsV3'
import { selectShowModalWithParams } from 'state/app/selectors'
import { withdrawCrossMargin } from 'state/futures/crossMargin/actions'
import {
	selectCollateralBalances,
	selectCrossMarginActivePositions,
	selectV3SynthPrices,
	selectWithdrawableCrossMargin,
} from 'state/futures/crossMargin/selectors'
import { selectIsSubmittingIsolatedTransfer } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import AssetSelector from './AssetSelector'
import MaxButton from './MaxButton'

const WithdrawCrossMargin = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const submitting = useAppSelector(selectIsSubmittingIsolatedTransfer)
	const collateralBalances = useAppSelector(selectCollateralBalances)
	const maxWithdrawUSD = useAppSelector(selectWithdrawableCrossMargin)
	const indexPrices = useAppSelector(selectV3SynthPrices)
	const activePositions = useAppSelector(selectCrossMarginActivePositions)
	const modalParams = useAppSelector(selectShowModalWithParams)?.params

	const [selectedAsset, setSelectedAsset] = useState<V3SynthMarketKey>('SNXUSD')
	const [amount, setAmount] = useState('')

	useEffect(() => {
		const asset = modalParams?.asset
		if (V3_SYNTH_MARKET_KEYS.includes(asset)) {
			setSelectedAsset(asset)
		}
	}, [modalParams?.asset])

	const selectedAssetBalance = useMemo(
		() => wei(collateralBalances[selectedAsset] ?? '0'),
		[selectedAsset, collateralBalances]
	)

	const selectedAssetPrice = useMemo(() => {
		const price = indexPrices[v3SynthKeyToV2Asset(selectedAsset)]
		return price ?? wei(0)
	}, [indexPrices, selectedAsset])

	const maxWithdraw = useMemo(() => {
		if (selectedAsset === 'SNXUSD') {
			return maxWithdrawUSD.gt(selectedAssetBalance) ? selectedAssetBalance : maxWithdrawUSD
		}
		if (!activePositions.length) {
			// If there are no open positions the user can withdraw all of their collateral
			return selectedAssetBalance
		}
		const maxWithdrawAsset = selectedAssetPrice.gt(0)
			? maxWithdrawUSD.div(selectedAssetPrice)
			: wei(0)
		return maxWithdrawAsset.gt(selectedAssetBalance) ? selectedAssetBalance : maxWithdrawAsset
	}, [
		maxWithdrawUSD,
		selectedAsset,
		selectedAssetPrice,
		selectedAssetBalance,
		activePositions.length,
	])

	const withdrawDisabled = useMemo(() => {
		if (!amount || Number(amount) === 0 || submitting || maxWithdraw.lt(amount)) {
			return true
		}
		return false
	}, [amount, submitting, maxWithdraw])

	const value = useMemo(
		() => V3_DEPOSIT_OPTIONS.find((option) => option.value === selectedAsset),
		[selectedAsset]
	)

	const onSelectAsset = useCallback((asset: ValueType<any>) => {
		setAmount('')
		setSelectedAsset(asset.value as V3SynthMarketKey)
	}, [])

	const handleSetMax = useCallback(() => {
		setAmount(stripZeros(maxWithdraw.toString()))
	}, [maxWithdraw])

	const onWithdraw = () => {
		dispatch(withdrawCrossMargin({ amount: wei(amount), asset: selectedAsset }))
	}

	return (
		<>
			<AssetSelector onSelectAsset={onSelectAsset} selectedValue={value} />
			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
				<BalanceText>
					<span>{formatNumber(maxWithdraw, { suggestDecimals: true })}</span>{' '}
					{V3_DEPOSITABLE_ASSETS[selectedAsset]}
				</BalanceText>
			</BalanceContainer>
			<NumericInput
				dataTestId="futures-market-trade-withdraw-margin-input"
				placeholder={'0.00'}
				value={amount}
				onChange={(_, v) => setAmount(v)}
				right={
					<MaxButton onClick={handleSetMax}>{t('futures.market.trade.margin.modal.max')}</MaxButton>
				}
			/>
			<Spacer height={20} />

			<TransactionButton
				data-testid="futures-market-trade-deposit-withdraw-button"
				disabled={withdrawDisabled}
				loading={submitting}
				fullWidth
				onClick={onWithdraw}
				variant="flat"
			>
				{t('futures.market.trade.margin.modal.withdraw.button')}
			</TransactionButton>
		</>
	)
})

export default WithdrawCrossMargin

export const BalanceContainer = styled(FlexDivRowCentered)`
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`
